import DynamicForm from "../../../../components/FormElements/DynamicForm";
import * as Yup from "yup";
import { Col, Row } from "antd";
import logo from "../../../../assets/images/logo.png";
import AuthLayout from "../../../../components/Layout/AuthLayout";
import "./style.css";
import Button from "../../../../components/FormElements/Button";
import { Link, useNavigate } from "react-router-dom";
import { FaEnvelope, FaEye } from "react-icons/fa";
import { authTypes, socialLogin } from "../../../../config/utils/constants";
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "../../../../config/firebase/firebase";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAccessToken,
  updateLoggedInUser,
  updateLoginStatus,
} from "../../../../store/common/authSlice";
import useAuthApi from "../../../../config/hooks/api/auth/useAuthApi";
import {
  setAppLoading,
  setError,
  setErrorData,
  setMessage,
  setSwRegistrationForLoginCase,
} from "../../../../store/common/generalSlice";
import userLocationRetrieve from "../../../../config/utils/UserLocationRetrieve";
import ReverseGeocoding from "../../../../config/utils/ReverseGeoCoding";
import {
  generateFallbackDeviceToken,
  getFcmToken,
} from "../../../../config/utils";
import { ensureServiceWorkerRegistration } from "../../../../config/utils/serviceWorkerRegistration";

const SignIn = () => {
  const generalState = useSelector((state) => state.general);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { signIn, socialSignUp } = useAuthApi();

  // const [verifiedUserData, setVerifiedUserData] = useState(null)
  const [initialData, setInitialData] = useState({
    email: "",
  });

  useEffect(() => {
    let user = JSON.parse(localStorage.getItem("tempUser"));
    if (user) {
      const { email } = user;
      setInitialData({
        email: email,
      });
      localStorage.removeItem("tempUser");
    }
  }, []);

  // Define the form configuration
  const form = {
    name: "loginForm",
    formFields: [
      {
        name: "email",
        type: "email",
        label: { value: "Email Address", style: "", classes: "" },
        placeholder: "Email",
        width: { md: 24, sm: 24, xs: 24 },
        component: null,
        classes: "icon-primary",
        icon: <FaEnvelope />,
      },
      {
        name: "password",
        type: "password",
        label: { value: "Password", style: "", classes: "" },
        placeholder: "Password",
        width: { md: 24, sm: 24, xs: 24 },
        component: null,
        classes: "icon-primary",
        // icon: <FaEye />,
      },
    ],
    formValidation: {
      email: Yup.string()
        .email("Email is invalid")
        .required("Email is required"),
      password: Yup.string().required("Password is required"),
    },
  };

  const handleSubmit = async (values) => {
    try {
      if (!values || !Object.keys(values)?.length) {
        dispatch(setError("Fields should not be empty"));
        return;
      }
      // checking registration for sw exists or not, for a case where logout
      // and redirect to login screen so without refreshing the app user will
      // not have the registration available for sw which we setup on index.js
      const swRegistration = await ensureServiceWorkerRegistration();
      let deviceToken = await getFcmToken(swRegistration);

      // a case if device token is null means the device is not supported then a temp device token
      // generated to let the user login at least.
      if (deviceToken === null) {
        const fallbackToken = generateFallbackDeviceToken();
        if (fallbackToken) {
          localStorage.setItem("fcmToken", fallbackToken);
          // dispatch(setMessage("Alternative notification system activated."));
          deviceToken = fallbackToken;
        }
      }

      // supported device but notifications are disable.
      else if (!deviceToken) {
        return;
      }

      dispatch(setAppLoading(true));
      const { email, password } = values;

      const response = await signIn({ email, password, deviceToken });
      if (response) {
        dispatch(setAppLoading(false));
        if (!response?.data?.token || !response?.data?.user) {
          if (!response?.data?.token)
            dispatch(setError("Required access token"));
          else if (!response?.data?.user)
            dispatch(setError("Required user data"));
          else dispatch(setError("Required access token and user data"));
          return;
        }

        dispatch(setMessage(response?.data?.message));

        localStorage.setItem("accessToken", response?.data?.token);
        localStorage.setItem("user", JSON.stringify(response?.data?.user));

        // just to show business modal popup once, on homepage of a sp.
        if (response?.data?.user?.type == "Service Provider") {
          localStorage.setItem("businessModal", true);
        }

        dispatch(updateLoginStatus(true));
        dispatch(updateLoggedInUser(response?.data?.user));
        dispatch(updateAccessToken(response?.data?.token));

        dispatch(setMessage(response?.data?.message));
      } else {
        localStorage.removeItem("fcmToken");
      }
    } catch (error) {
      console.log(error);
      dispatch(setAppLoading(false));
    }
  };

  const submitForm = () => {
    document
      .querySelector("form")
      ?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  };

  const handleSocialLogin = async (loginType) => {
    try {
      if (loginType) {
        dispatch(setAppLoading(true));

        let provider, result;
        if (loginType === "facebook") {
          provider = new FacebookAuthProvider();

          result = await signInWithPopup(auth, provider);
        } else if (loginType === "apple") {
          provider = new OAuthProvider("apple.com");
          provider.addScope("email");
          provider.addScope("name");

          result = await signInWithPopup(auth, provider);
        } else if (loginType === "google") {
          provider = new GoogleAuthProvider();

          provider.setCustomParameters({
            prompt: "select_account", // Forces the account selection screen
          });

          result = await signInWithPopup(auth, provider);
        }

        if (result) {
          const { user } = result;
          const {
            displayName: fullName,
            email,
            providerData,
            accessToken: socialToken,
          } = user;
          const {
            city,
            country,
            lat: latitude,
            lng: longitude,
          } = await getCurrentLocation();
          const fcmToken = await getFcmToken();

          // a case if device token is null means the device is not supported then a temp device token
          // generated to let the user login at least.
          if (fcmToken === null) {
            const fallbackToken = generateFallbackDeviceToken();
            if (fallbackToken) {
              localStorage.setItem("fcmToken", fallbackToken);
              // dispatch(setMessage("Alternative notification system activated."));
              fcmToken = fallbackToken;
            }
          }

          // supported device but notifications are disable.
          else if (!fcmToken) {
            dispatch(setError("Required fcm token"));
            dispatch(setAppLoading(false));
            return;
          }

          const payload = {
            fullName,
            email,
            longitude,
            latitude,
            socialId: providerData?.length && providerData[0].uid,
            socialPlatform: loginType,
            city,
            country,
            platform: "web",
            fcmToken,
            socialToken,
            type: "Service User",
          };
          const response = await socialSignUp(payload);
          if (response) {
            if (response?.data) {
              const user = structuredClone(response?.data?.data);
              user["provider"] = loginType;
              delete user.authToken;

              localStorage.setItem(
                "accessToken",
                response?.data?.data?.authToken,
              );
              localStorage.setItem("user", JSON.stringify(user));

              // just to show business modal popup once, on homepage of a sp.
              if (user?.type == "Service Provider") {
                localStorage.setItem("businessModal", true);
              }

              dispatch(updateLoginStatus(true));
              dispatch(updateLoggedInUser(user));
              dispatch(updateAccessToken(response?.data?.data?.authToken));
              dispatch(setAppLoading(false));

              // if user login through socials then it will be redirect to update its profile.
              if (user && (!user?.phoneNo || !user?.address)) {
                navigate("/settings/editProfile");
                dispatch(
                  setMessage("Login successful, kindly Update your profile"),
                );
              }
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
      dispatch(setAppLoading(false));
    }
  };

  const getCurrentLocation = async () => {
    const coords = await userLocationRetrieve();
    if (coords) {
      const location = await ReverseGeocoding({
        lat: coords?.latitude,
        lng: coords?.longitude,
      });

      if (location) {
        return {
          lat: coords?.latitude,
          lng: coords?.longitude,
          address: location?.formatted_address,
          postalCode: location?.postalCode,
          town: location?.town,
          city: location?.city,
          country: location?.country,
        };
      }
    }
  };

  useEffect(() => {
    //if account is unverified on login redirect to verify otp
    if (generalState) {
      const { errorData } = generalState;
      if (errorData?.data?.status === 426) {
        const tempData = {};
        tempData.type = authTypes.indexOf("signUp");
        localStorage.setItem("tempUser", JSON.stringify(tempData));
        navigate("/verify-otp");
        setTimeout(() => {
          dispatch(setErrorData(""));
        }, 2000);
      }
    }
  }, [generalState?.errorData]);

  // useEffect(() => {
  //   //if re routed after otp verification
  //   const userData = JSON.parse(localStorage.getItem('tempUser'))
  //   if(userData?.email){
  //     const { email } = userData
  //     setVerifiedUserData({ email })

  //     localStorage.removeItem('tempUser')
  //   }
  // }, [])

  return (
    <AuthLayout>
      <Row className="flex justify-center login-screen custom-card-boxshadow px-4 py-5 rounded-md">
        <Col xs={24} sm={18} md={18} className="form-section">
          <img src={logo} alt="logo" className="img-fluid logo mb-8" />
          <h4 className="heading-4 font-bold mb-4">Login</h4>
          <p className="text-slate-500 mb-6">
            Vehicle Experts - Maximize sales
          </p>
          <DynamicForm
            onSubmit={handleSubmit}
            form={form}
            editData={initialData}
          />
          <div className="flex justify-start w-full mt-2 mb-6">
            <Button.Basic
              onClick={submitForm}
              disabled={false}
              icon={null}
              loading={false}
              type="primary"
              size="medium"
              text="Login"
              className="btn-primary md:w-fit sm:w-fit w-full"
            />
          </div>
          <div className="text-slate-300 dashed-line w-full mb-3"></div>
          <Row className="mb-3 w-full">
            <Col
              md={14}
              sm={14}
              xs={24}
              className="md:text-left sm:text-left text-center"
            >
              <small className="text-slate-400">Don't have an account?</small>
              <Link to="/signup" className="ml-1 font-bold">
                Signup
              </Link>
            </Col>
            <Col
              md={10}
              sm={10}
              xs={24}
              className="md:text-right sm:text-right text-center"
            >
              <Link className="text-slate-300 text-xs" to="/forgot-password">
                Forgot Password?
              </Link>
            </Col>
          </Row>
          <div className="text-slate-300 dashed-line w-full mb-6"></div>
          <div className="flex justify-center">
            <div>
              <p className="text-xs text-slate-400 mb-2">Or Continue With</p>
              <div className="flex justify-between">
                {socialLogin.map((item) => (
                  <span
                    onClick={() => handleSocialLogin(item.name)}
                    key={item.name}
                  >
                    {item.icon}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </AuthLayout>
  );
};

export default SignIn;

const CustomAppleLoginButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      style={{
        backgroundColor: "#000", // Customize button background color
        color: "#fff", // Customize text color
        border: "none",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        fontSize: "16px", // Customize text size
        cursor: "pointer",
      }}
    >
      {socialLogin.filter((e) => e.name == "apple")[0].icon}
    </button>
  );
};

export { CustomAppleLoginButton };
